import { DEFAULTS, SITE_NAMES } from '@/constants';
import { fetchLayoutData } from '@/services/layout';
import type { AppContext, SitecoreContext, SitecoreContextData, SitecoreRoute } from '@/types';
import { initialAppContext } from '@/utils/context';
import type { SubState } from '@shared-jss/types';
import { isClient } from '@shared-jss/utils/env';
import { getLanguageCode } from '@shared-jss/utils/language';
import type { LayoutServiceData } from '@sitecore-jss/sitecore-jss-vue';
import type { Nullable } from 'design.zorgdomein/dist/typings/types/generics';
import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';
import useLoginStore from './login';
import useProductStore from './product';

const useSitecoreStore = defineStore(
    'sitecore',
    () => {
        const productStore = useProductStore();
        const loginStore = useLoginStore();
        const appContext = ref<AppContext>({ ...initialAppContext });
        const context = ref<Nullable<SitecoreContext>>(null);
        const routing = reactive<SubState<SitecoreRoute>>({
            data: null,
            loading: true,
            error: false
        });
        const proposition = ref('');

        const language = computed(() => context.value?.language || DEFAULTS.language);
        const languageCode = computed(() => getLanguageCode(language.value));
        const pageEditing = computed(() => !!context.value?.pageEditing);
        const siteName = computed(() => {
            const key = context.value?.site?.name as keyof typeof SITE_NAMES;

            return SITE_NAMES[key] ?? '';
        });

        const setAppContext = (newAppContext: AppContext) => {
            if (newAppContext.proposition !== proposition.value) {
                productStore.clearStore();
            }

            appContext.value = newAppContext;
            proposition.value = newAppContext.proposition;
        };

        const getSitecoreData = async (itemPath: string) => {
            routing.loading = true;
            routing.error = false;

            const sitecoreData = await fetchLayoutData(appContext.value, itemPath);

            await loginStore.getLoginInfo(true);

            // Clear products and re-fetch with correct data when:
            // - Login has changed
            // - Language has changed
            if (
                loginStore.loginInfo.changed ||
                sitecoreData?.sitecore.context?.language !== context.value?.language
            ) {
                productStore.clearStore();
            }

            if (!sitecoreData) {
                routing.error = true;
            }

            setSitecoreData(sitecoreData);

            routing.loading = false;
        };
        const setSitecoreData = (sitecoreData: Nullable<LayoutServiceData>) => {
            const routeData: Nullable<LayoutServiceData['sitecore']['route']> =
                sitecoreData?.sitecore?.route || null;
            const contextData: Nullable<SitecoreContextData> =
                (sitecoreData?.sitecore?.context as SitecoreContextData) || null;

            context.value = contextData
                ? {
                      ...contextData,
                      routeName: routeData?.name ?? '',
                      itemId: routeData?.itemId ?? ''
                  }
                : null;

            routing.data = routeData
                ? ({
                      ...routeData,
                      routeUrl: isClient() ? location.href : '/'
                  } as SitecoreRoute)
                : null;
        };

        return {
            appContext,
            context,
            language,
            languageCode,
            pageEditing,
            siteName,
            routing,
            proposition,
            setAppContext,
            getSitecoreData,
            setSitecoreData
        };
    },
    {
        persist: {
            pick: ['context.language', 'proposition']
        }
    }
);

export default useSitecoreStore;
